/** One file's extracted clauses: what the document asks, as a Checklist tab of our own
 * obligations by the goals and sections it states (the clauses we do not assess in bands
 * below it) and a Whole document tab. No project's verdicts: "Assessed on" names each
 * project that assessed it, linking to that specialist's card there. Commentable line by
 * line like a briefing: a comment goes to a project's review conversation, where the
 * clauses are revised against it. See docs/Y2026-Q4/design-document-registers.md. */
import { type ReactNode, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";

import { useClauseComments, useGlobalRegisters, useRegister } from "../api";
import type {
  AssessedOn,
  DocumentRegister,
  GroupStatus,
  LineRef,
  RegisterLine,
  RegisterView,
} from "../api";
import { useProject } from "../api/hooks/useProject";
import { useAgentLabel } from "../api/hooks/useStatefulAgents";
import { CommentBox, CommentsPanel, Contradictions } from "../components/ClauseComments";
import { ClauseFilterBar } from "../components/ClauseFilters";
import {
  type FilterDim,
  filterClauses,
  groupDim,
  useClauseFilters,
} from "../components/clauseFilterModel";
import {
  Band,
  ClauseTree,
  CollapseScope,
  MilestonesThenDocument,
  type RowContext,
} from "../components/ClauseGroups";
import {
  ClauseDetails,
  ClauseLabels,
  ClauseRow,
  CountsLine,
  ListItems,
  StatusLegend,
} from "../components/ClauseParts";
import styles from "../components/Clauses.module.css";
import {
  BAND_ORDER,
  BAND_TITLE,
  type Band as BandKey,
  type Counts,
  assessedBy,
  isIrrelevant,
  listGoalOf,
  placeOf,
  rowTitle,
  sectionGrouping,
  shapeLabels,
} from "../components/clauseStatus";
import { PageHeader } from "../components/PageHeader";
import { scopeText } from "../components/registerChip";
import { SourceTextModal } from "../components/SourceTextModal";
import { ViewAiTraceButton } from "../components/ViewAiTraceButton";
import { formatWhen } from "../utils/formatWhen";
import { projectDisplayName } from "../utils/projectName";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import { useScrollToHash } from "../utils/useScrollToHash";
import { documentClausesPath, registerPath } from "./learningAgentPaths";

const REGISTER_TRACE_SURFACE = "document_register";

/** Only what changes how to read the list: one in no role, or one since replaced. */
const RoleNote = ({ register }: { register: DocumentRegister }) => {
  const slot = register.slot;
  if (!slot) {
    return (
      <p className={styles.note}>
        <span className={`${styles.chip} ${styles.chipWarn}`}>Not in a role</span> No agent
        assesses these clauses until an agent files the document in its role.
      </p>
    );
  }
  if (slot.active) return null;
  return (
    <p className={styles.note}>
      <span className={`${styles.chip} ${styles.chipWarn}`}>Replaced</span>{" "}
      {slot.retired_at ? `${formatWhen(slot.retired_at)}: ` : ""}
      {slot.retired_reason}
    </p>
  );
};

/** Where the file sits: Global data's scope, or the project it was uploaded to. */
const useScope = (register: DocumentRegister): string => {
  const global = register.document_kind === "global";
  const globals = useGlobalRegisters({ enabled: global });
  const project = useProject(global ? undefined : (register.project_id ?? undefined));
  if (global) {
    const listed = globals.data?.documents.find((d) => d.document_id === register.document_id);
    return listed ? `Global data: ${scopeText(listed.scope)}` : "Global data";
  }
  return `Uploaded to ${project.data ? projectDisplayName(project.data) : "its project"}`;
};

const RegisterHeader = ({
  register,
  versions,
  onVersion,
  children,
}: {
  register: DocumentRegister;
  versions: DocumentRegister[];
  onVersion: (registerId: string) => void;
  children: ReactNode;
}) => {
  // The file open in the viewer: a folder source opens one of its files.
  const [viewing, setViewing] = useState<string | null>(null);
  const scope = useScope(register);
  const counts = register.counts;
  const global = register.document_kind === "global";
  return (
    <div className={styles.card}>
      <div className={styles.meta}>
        {register.slot && <span className={styles.chip}>{register.slot.role_label}</span>}
        <span className={styles.chip}>{scope}</span>
        <label>
          Version{" "}
          <select value={register.register_id} onChange={(e) => onVersion(e.target.value)}>
            {versions.map((v) => (
              <option key={v.register_id} value={v.register_id}>
                v{v.version} ({v.status})
              </option>
            ))}
          </select>
        </label>
        {register.status === "ready" ? (
          register.published_at && <span>Published {formatWhen(register.published_at)}</span>
        ) : (
          <span
            className={`${styles.chip} ${
              register.status === "failed" ? styles.chipBad : styles.chipWarn
            }`}
          >
            {register.status === "building" ? "Being built" : "Failed"}
          </span>
        )}
        {register.files.length ? (
          <label>
            Open a file{" "}
            <select value="" onChange={(e) => setViewing(e.target.value || null)}>
              <option value="">{register.files.length} files</option>
              {register.files.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setViewing(register.document_id)}
          >
            Open the file
          </button>
        )}
        <ViewAiTraceButton
          surface={REGISTER_TRACE_SURFACE}
          artefactId={register.register_id}
          hasTrace={register.has_trace}
        />
      </div>
      <p className={styles.note}>
        {counts.lines ?? 0} clauses, {counts.requirements ?? 0} requirements
        {counts.goals ? `, ${counts.goals} goal${counts.goals === 1 ? "" : "s"} the document states` : ""}
        {counts.unclassified ? `, ${counts.unclassified} sections never split` : ""}.
      </p>
      {children}
      {register.stale && (
        <p className={styles.note}>
          <span className={`${styles.chip} ${styles.chipWarn}`}>Out of date</span> The
          document has changed since these clauses were extracted. The next agent to read it
          extracts them from the new text.
        </p>
      )}
      {register.status === "building" && (
        <p className={styles.note}>
          Being built now. This page refreshes when it is ready.
        </p>
      )}
      {register.status === "failed" && register.error && (
        <p className={styles.note}>
          The build failed: {register.error}. The next request retries it.
        </p>
      )}
      {counts.outline_error && (
        <p className={styles.note}>
          The document's goals, links and forms could not be read: {counts.outline_error}
        </p>
      )}
      {viewing && (
        <SourceTextModal
          uploadId={global ? undefined : viewing}
          globalDataId={global ? viewing : undefined}
          hasSourceText
          sourceLabel={register.files.find((f) => f.id === viewing)?.name ?? register.document_name}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

/** Which specialist assesses which topics, and the topics nobody assesses yet. */
const AssessorMap = ({
  topics,
  agentLabel,
}: {
  topics: RegisterView["topics"];
  agentLabel: (agent: string) => string;
}) => {
  const assessors = [...new Set(topics.flatMap((t) => (t.assessor ? [t.assessor] : [])))];
  const groups = [
    ...assessors.map((a) => ({
      name: agentLabel(a),
      topics: topics.filter((t) => t.assessor === a).map((t) => t.label),
    })),
    { name: "Nobody yet", topics: topics.filter((t) => !t.assessor).map((t) => t.label) },
  ].filter((g) => g.topics.length > 0);
  return (
    <section aria-labelledby="who-assesses-heading" className={styles.assessorMap}>
      <h3 id="who-assesses-heading" className={styles.subhead}>
        Who assesses what
      </h3>
      <div className={styles.assessorColumns}>
        {groups.map((g) => (
          <div key={g.name}>
            <strong>{g.name}</strong>
            <ul>
              {g.topics.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

/** The projects that have assessed against this list, each closed to its clauses by
 * status, opening to each specialist's, linked to its card on that project. */
const AssessedOnPanel = ({
  data,
  agentLabel,
}: {
  data: RegisterView;
  agentLabel: (agent: string) => string;
}) => {
  const projects = new Map<string, AssessedOn[]>();
  for (const a of data.assessed_on) {
    projects.set(a.project_id, [...(projects.get(a.project_id) ?? []), a]);
  }
  return (
    <section aria-labelledby="assessed-on-heading" className={styles.card}>
      <h3 id="assessed-on-heading" className={styles.subhead}>
        Assessed on
      </h3>
      {projects.size === 0 ? (
        <p className={styles.note}>No project has assessed against this list yet.</p>
      ) : (
        <>
          <ul className={styles.assessedOn}>
            {[...projects.values()].map((agents) => {
              const total: Counts = {};
              for (const a of agents) {
                for (const [status, n] of Object.entries(a.counts) as [GroupStatus, number][]) {
                  total[status] = (total[status] ?? 0) + n;
                }
              }
              return (
                <li key={agents[0].project_id}>
                  <details>
                    <summary>
                      {agents[0].project_name} - <CountsLine counts={total} empty="" />
                    </summary>
                    <ul className={styles.assessedOn}>
                      {agents.map((a) => (
                        <li key={a.agent}>
                          <Link
                            to={documentClausesPath(
                              a.project_id,
                              a.agent,
                              data.register.document_id,
                            )}
                          >
                            {agentLabel(a.agent)}
                          </Link>
                          {" - "}
                          <CountsLine counts={a.counts} empty="" />
                        </li>
                      ))}
                    </ul>
                  </details>
                </li>
              );
            })}
          </ul>
          <StatusLegend />
        </>
      )}
    </section>
  );
};

const CLASSIFICATION_LABEL: Record<string, string> = {
  requirement: "Requirement",
  definition: "Definition",
  heading: "Heading",
  informative: "Informative",
  unclassified: "Never split",
  irrelevant: "Not part of the obligations",
};

const OURS = "Our obligation";

const NO_CONTEXT: RowContext = { topic: null, form: null, assessedBy: null };

type Tab = "checklist" | "document" | "comments";

const TABS: [Tab, string][] = [
  ["checklist", "Checklist"],
  ["document", "Whole document"],
  ["comments", "Comments"],
];

export const RegisterRoute = () => {
  const { documentId = "", projectId = null } = useParams();
  const [registerId, setRegisterId] = useState<string | null>(null);
  // In the URL, so a goal's source link can open the whole document at its clause.
  const [search, setSearch] = useSearchParams();
  const view = search.get("view");
  const tab: Tab = view === "document" || view === "comments" ? view : "checklist";
  const setTab = (next: Tab) =>
    setSearch(next === "checklist" ? {} : { view: next }, { replace: true });
  const [checklistFilters, setChecklistFilters] = useClauseFilters();
  const [documentFilters, setDocumentFilters] = useClauseFilters();
  const query = useRegister(documentId, registerId);
  const { agentLabel } = useAgentLabel();
  const data = query.data;
  useScrollToHash(!!data);
  useDocumentTitle(
    data ? `Extracted clauses - ${data.register.document_name}` : "Extracted clauses",
  );
  const comments = useClauseComments(documentId);
  const topicLabel = (key: string) => data?.topics.find((t) => t.key === key)?.label ?? key;

  const dims = useMemo(() => {
    const topics: FilterDim<RegisterLine> = {
      key: "topic",
      noun: { one: "topic", many: "topics" },
      values: (l) => (l.topics.length ? l.topics : ["none"]),
      labelOf: (v) =>
        v === "none" ? "No topic" : (data?.topics.find((t) => t.key === v)?.label ?? v),
    };
    const checklist: FilterDim<RegisterLine>[] = [
      {
        key: "specialist",
        noun: { one: "specialist", many: "specialists" },
        values: (l) => (l.assessors.length ? l.assessors : ["nobody"]),
        labelOf: (v) => (v === "nobody" ? "Nobody yet" : agentLabel(v)),
      },
      topics,
      groupDim(sectionGrouping(data?.goals ?? [], data?.groups ?? [])),
      {
        key: "kind",
        noun: { one: "kind", many: "kinds" },
        values: (l) => (shapeLabels(l).length ? shapeLabels(l) : [OURS]),
        labelOf: (v) => v,
      },
    ];
    const document: FilterDim<RegisterLine>[] = [
      {
        key: "type",
        noun: { one: "type", many: "types" },
        values: (l) => [l.classification],
        labelOf: (v) => CLASSIFICATION_LABEL[v] ?? v,
        order: Object.keys(CLASSIFICATION_LABEL),
      },
      topics,
    ];
    return { checklist, document };
  }, [data, agentLabel]);

  if (query.isLoading) return <p>Loading the extracted clauses…</p>;
  if (query.isError || !data) {
    return <p>This has no extracted clauses.</p>;
  }
  if (projectId && data.register.document_kind === "global") {
    return <Navigate to={registerPath(documentId, null)} replace />;
  }
  const byId = new Map(data.lines.map((l) => [l.line_id, l]));
  const otherOptions = (line: RegisterLine): LineRef[] =>
    line.one_of
      ? data.lines.filter((l) => l.one_of === line.one_of && l.line_id !== line.line_id)
      : [];
  const row = (line: RegisterLine, wholeDocument: boolean, context: RowContext) => {
    const range = line.file
      ? `${line.file.name} p${line.file.page}`
      : line.start_ref === line.end_ref
        ? line.start_ref
        : `${line.start_ref} - ${line.end_ref}`;
    const requirement = line.assessable || line.standing !== null;
    return (
      <ClauseRow
        // Two clauses of one text (a certificate article repeated) share a line_id.
        key={line.position}
        line={line}
        marker={
          wholeDocument ? (
            <span className={styles.tag}>{CLASSIFICATION_LABEL[line.classification]}</span>
          ) : (
            <span className={styles.neutralMarker} aria-hidden="true">
              ▢
            </span>
          )
        }
        title={
          requirement
            ? rowTitle(line)
            : isIrrelevant(line)
              ? `Not part of the obligations: ${line.reason}`
              : line.reason
        }
        labels={
          <ClauseLabels
            line={line}
            options={otherOptions(line).length + 1}
            agentLabel={agentLabel}
            showSection={false}
            viewer={null}
          />
        }
        trailing={
          requirement && !context.assessedBy ? (
            <span className={styles.muted}>{assessedBy(line, agentLabel)}</span>
          ) : null
        }
        below={
          line.entry === "list" ? (
            <ListItems
              entry={line}
              goal={listGoalOf(line, data.goals)}
              lineOf={(id) => byId.get(id)}
              itemOf={undefined}
              details={(member) => (
                <ClauseDetails
                  line={member}
                  otherOptions={[]}
                  definitionTexts={data.definition_texts}
                  refHref={(id) => `#${id}`}
                  agentLabel={agentLabel}
                  topicLabel={topicLabel}
                  why={null}
                />
              )}
            />
          ) : undefined
        }
        details={() => (
          <>
            <ClauseDetails
              line={line}
              otherOptions={otherOptions(line)}
              definitionTexts={data.definition_texts}
              refHref={(id) => `#${id}`}
              agentLabel={agentLabel}
              topicLabel={topicLabel}
              why={null}
            />
            <CommentBox
              documentId={documentId}
              registerId={data.register.register_id}
              kind="clause"
              lineIds={[line.line_id]}
              label={`Comment on ${line.reference || range}`}
              placeholder="What is wrong with how this clause was read: split, merged, the wrong party, not an obligation…"
            />
          </>
        )}
      />
    );
  };
  const checklistLines = data.lines.filter((l) => placeOf(l) !== null);
  const kept = filterClauses(checklistLines, dims.checklist, checklistFilters);
  const placed = (where: BandKey | "checklist") => kept.filter((l) => placeOf(l) === where);
  const documentLines = filterClauses(data.lines, dims.document, documentFilters);
  return (
    <section>
      <PageHeader
        title={`Extracted clauses: ${data.register.document_name}${
          data.register.files.length ? " (folder)" : ""
        }`}
        description={`${
          data.register.files.length
            ? `What this folder's ${data.register.files.length} files require, read as one document.`
            : "What this file requires."
        }`}
        actions={
          <Link to={projectId ? `/projects/${projectId}/files` : "/global-data"}>
            Back to {projectId ? "the project's files" : "global data"}
          </Link>
        }
      />
      <RegisterHeader register={data.register} versions={data.versions} onVersion={setRegisterId}>
        <RoleNote register={data.register} />
        <AssessorMap topics={data.topics} agentLabel={agentLabel} />
      </RegisterHeader>
      <AssessedOnPanel data={data} agentLabel={agentLabel} />
      {/* A version still building, or one that failed, has no clauses to read yet: the
          header says so. */}
      {data.lines.length === 0 && data.register.status !== "ready" ? null : (
        <>
        <div className={styles.card}>
          <div role="tablist" aria-label="How to read the clauses" className={styles.tabs}>
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                id={`clauses-tab-${key}`}
                aria-selected={tab === key}
                aria-controls={`clauses-panel-${key}`}
                tabIndex={tab === key ? 0 : -1}
                className={`${styles.tab} ${tab === key ? styles.tabActive : ""}`}
                onClick={() => setTab(key)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                    const next = TABS[(TABS.findIndex(([k]) => k === key) + 1) % TABS.length][0];
                    setTab(next);
                    document.getElementById(`clauses-tab-${next}`)?.focus();
                  }
                }}
              >
                {label}
                {key === "comments" && comments.data ? ` (${comments.data.comments.length})` : ""}
              </button>
            ))}
          </div>
          <div
            role="tabpanel"
            id={`clauses-panel-${tab}`}
            aria-labelledby={`clauses-tab-${tab}`}
          >
            <p className={styles.note}>
              {tab === "checklist"
                ? "What the document requires of us. Everything else is in the bands below. Open a clause to comment on it."
                : tab === "document"
                  ? "Every clause in the document, in order."
                  : null}
            </p>
            {tab === "comments" ? (
              <CommentsPanel
                documentId={documentId}
                registerId={data.register.register_id}
                comments={comments.data?.comments}
              />
            ) : tab === "checklist" ? (
              <>
                <Contradictions
                  documentId={documentId}
                  register={data.register}
                  lines={data.lines}
                />
                <ClauseFilterBar
                  lines={checklistLines}
                  dims={dims.checklist}
                  state={checklistFilters}
                  onChange={setChecklistFilters}
                  shown={kept.length}
                />
                <CollapseScope>
                  {placed("checklist").length === 0 ? (
                    <p className={styles.note}>No obligation of ours matches.</p>
                  ) : (
                    <MilestonesThenDocument
                      lines={placed("checklist")}
                      allLines={data.lines}
                      goals={data.goals}
                      groups={data.groups}
                      sourceHref={(id) => `?view=document#${id}`}
                      renderLine={(l, context) => row(l, false, context)}
                      assessedByOf={(l) => assessedBy(l, agentLabel)}
                    />
                  )}
                  {BAND_ORDER.map((band) => {
                    const members = placed(band);
                    // Contradictions have their own section at the top.
                    if (band === "conflicts" || members.length === 0) return null;
                    return (
                      <Band
                        key={band}
                        title={BAND_TITLE[band]}
                        count={members.length}
                        holds={new Set(members.map((m) => m.line_id))}
                      >
                        <ul className={styles.list}>
                          {members.map((l) => row(l, false, NO_CONTEXT))}
                        </ul>
                      </Band>
                    );
                  })}
                </CollapseScope>
              </>
            ) : (
              <>
                <ClauseFilterBar
                  lines={data.lines}
                  dims={dims.document}
                  state={documentFilters}
                  onChange={setDocumentFilters}
                  shown={documentLines.length}
                />
                <CollapseScope>
                  <ClauseTree
                    lines={documentLines}
                    allLines={data.lines}
                    goals={[]}
                    groups={data.groups}
                    sourceHref={(id) => `?view=document#${id}`}
                    renderLine={(l, context) => row(l, true, context)}
                    assessedByOf={(l) => assessedBy(l, agentLabel)}
                  />
                </CollapseScope>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </section>
  );
};
